import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { BulkSelectionContextValue, SelectionState } from '@/types/ui';

// Actions for selection reducer
type SelectionAction = 
  | { type: 'SELECT_TICKET'; ticketId: string }
  | { type: 'SELECT_TICKETS'; ticketIds: string[] }
  | { type: 'DESELECT_TICKET'; ticketId: string }
  | { type: 'DESELECT_TICKETS'; ticketIds: string[] }
  | { type: 'SELECT_ALL'; totalCount: number }
  | { type: 'SELECT_NONE' }
  | { type: 'SELECT_RANGE'; ticketIds: string[] }
  | { type: 'TOGGLE_TICKET'; ticketId: string }
  | { type: 'SET_TOTAL_COUNT'; count: number }
  | { type: 'RESET' };

// Initial selection state
const initialState: SelectionState = {
  selectedTicketIds: new Set<string>(),
  isSelectingAll: false,
  selectedCount: 0,
  totalCount: 0,
  selectionAcrossPages: false,
  excludedIds: new Set<string>()
};

// Selection reducer
function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SELECT_TICKET': {
      if (state.selectedTicketIds.has(action.ticketId)) {
        return state; // Already selected
      }
      
      const newSelectedIds = new Set(state.selectedTicketIds);
      newSelectedIds.add(action.ticketId);
      
      // Remove from excluded if selecting all mode
      const newExcludedIds = new Set(state.excludedIds);
      if (state.isSelectingAll) {
        newExcludedIds.delete(action.ticketId);
      }
      
      return {
        ...state,
        selectedTicketIds: newSelectedIds,
        selectedCount: state.isSelectingAll 
          ? Math.max(0, state.totalCount - newExcludedIds.size)
          : newSelectedIds.size,
        excludedIds: newExcludedIds
      };
    }
    
    case 'SELECT_TICKETS': {
      const newSelectedIds = new Set(state.selectedTicketIds);
      const newExcludedIds = new Set(state.excludedIds);
      
      action.ticketIds.forEach(id => {
        if (!newSelectedIds.has(id)) {
          newSelectedIds.add(id);
        }
        if (state.isSelectingAll) {
          newExcludedIds.delete(id);
        }
      });
      
      return {
        ...state,
        selectedTicketIds: newSelectedIds,
        selectedCount: state.isSelectingAll 
          ? Math.max(0, state.totalCount - newExcludedIds.size)
          : newSelectedIds.size,
        excludedIds: newExcludedIds
      };
    }
    
    case 'DESELECT_TICKET': {
      if (!state.selectedTicketIds.has(action.ticketId) && !state.isSelectingAll) {
        return state; // Not selected
      }
      
      const newSelectedIds = new Set(state.selectedTicketIds);
      const newExcludedIds = new Set(state.excludedIds);
      
      if (state.isSelectingAll) {
        newExcludedIds.add(action.ticketId);
      } else {
        newSelectedIds.delete(action.ticketId);
      }
      
      return {
        ...state,
        selectedTicketIds: newSelectedIds,
        selectedCount: state.isSelectingAll 
          ? Math.max(0, state.totalCount - newExcludedIds.size)
          : newSelectedIds.size,
        excludedIds: newExcludedIds
      };
    }
    
    case 'DESELECT_TICKETS': {
      const newSelectedIds = new Set(state.selectedTicketIds);
      const newExcludedIds = new Set(state.excludedIds);
      
      action.ticketIds.forEach(id => {
        if (state.isSelectingAll) {
          newExcludedIds.add(id);
        } else {
          newSelectedIds.delete(id);
        }
      });
      
      return {
        ...state,
        selectedTicketIds: newSelectedIds,
        selectedCount: state.isSelectingAll 
          ? Math.max(0, state.totalCount - newExcludedIds.size)
          : newSelectedIds.size,
        excludedIds: newExcludedIds
      };
    }
    
    case 'SELECT_ALL': {
      return {
        ...state,
        isSelectingAll: true,
        selectionAcrossPages: true,
        selectedCount: action.totalCount,
        totalCount: action.totalCount,
        excludedIds: new Set<string>()
      };
    }
    
    case 'SELECT_NONE': {
      return {
        ...state,
        selectedTicketIds: new Set<string>(),
        isSelectingAll: false,
        selectionAcrossPages: false,
        selectedCount: 0,
        excludedIds: new Set<string>()
      };
    }
    
    case 'SELECT_RANGE': {
      const newSelectedIds = new Set(state.selectedTicketIds);
      action.ticketIds.forEach(id => newSelectedIds.add(id));
      
      return {
        ...state,
        selectedTicketIds: newSelectedIds,
        selectedCount: newSelectedIds.size,
        isSelectingAll: false,
        selectionAcrossPages: false
      };
    }
    
    case 'TOGGLE_TICKET': {
      const isSelected = state.isSelectingAll 
        ? !state.excludedIds.has(action.ticketId)
        : state.selectedTicketIds.has(action.ticketId);
      
      if (isSelected) {
        return selectionReducer(state, { type: 'DESELECT_TICKET', ticketId: action.ticketId });
      } else {
        return selectionReducer(state, { type: 'SELECT_TICKET', ticketId: action.ticketId });
      }
    }
    
    case 'SET_TOTAL_COUNT': {
      return {
        ...state,
        totalCount: action.count,
        selectedCount: state.isSelectingAll 
          ? Math.max(0, action.count - state.excludedIds.size)
          : state.selectedCount
      };
    }
    
    case 'RESET': {
      return initialState;
    }
    
    default:
      return state;
  }
}

// Context
const BulkSelectionContext = createContext<BulkSelectionContextValue | null>(null);

// Provider props
interface BulkSelectionProviderProps {
  children: React.ReactNode;
  maxSelectionLimit?: number;
  onSelectionChange?: (state: SelectionState) => void;
  onMaxSelectionExceeded?: (attemptedCount: number, limit: number) => void;
}

// Provider component
export const BulkSelectionProvider: React.FC<BulkSelectionProviderProps> = ({
  children,
  maxSelectionLimit = 500,
  onSelectionChange,
  onMaxSelectionExceeded
}) => {
  const [state, dispatch] = useReducer(selectionReducer, initialState);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange?.(state);
  }, [state, onSelectionChange]);

  // Check selection limits
  const checkSelectionLimit = useCallback((newCount: number): boolean => {
    if (newCount > maxSelectionLimit) {
      onMaxSelectionExceeded?.(newCount, maxSelectionLimit);
      return false;
    }
    return true;
  }, [maxSelectionLimit, onMaxSelectionExceeded]);

  const contextValue: BulkSelectionContextValue = {
    selectionState: state,
    
    selectTicket: useCallback((ticketId: string) => {
      if (!checkSelectionLimit(state.selectedCount + 1)) return;
      dispatch({ type: 'SELECT_TICKET', ticketId });
    }, [state.selectedCount, checkSelectionLimit]),
    
    selectTickets: useCallback((ticketIds: string[]) => {
      if (!checkSelectionLimit(state.selectedCount + ticketIds.length)) return;
      dispatch({ type: 'SELECT_TICKETS', ticketIds });
    }, [state.selectedCount, checkSelectionLimit]),
    
    deselectTicket: useCallback((ticketId: string) => {
      dispatch({ type: 'DESELECT_TICKET', ticketId });
    }, []),
    
    deselectTickets: useCallback((ticketIds: string[]) => {
      dispatch({ type: 'DESELECT_TICKETS', ticketIds });
    }, []),
    
    selectAll: useCallback(() => {
      if (!checkSelectionLimit(state.totalCount)) return;
      dispatch({ type: 'SELECT_ALL', totalCount: state.totalCount });
    }, [state.totalCount, checkSelectionLimit]),
    
    selectNone: useCallback(() => {
      dispatch({ type: 'SELECT_NONE' });
    }, []),
    
    selectRange: useCallback((startId: string, endId: string, allTicketIds: string[]) => {
      const startIndex = allTicketIds.indexOf(startId);
      const endIndex = allTicketIds.indexOf(endId);
      
      if (startIndex === -1 || endIndex === -1) return;
      
      const rangeStart = Math.min(startIndex, endIndex);
      const rangeEnd = Math.max(startIndex, endIndex);
      const rangeIds = allTicketIds.slice(rangeStart, rangeEnd + 1);
      
      if (!checkSelectionLimit(state.selectedCount + rangeIds.length)) return;
      dispatch({ type: 'SELECT_RANGE', ticketIds: rangeIds });
    }, [state.selectedCount, checkSelectionLimit]),
    
    toggleTicket: useCallback((ticketId: string) => {
      const isCurrentlySelected = state.isSelectingAll 
        ? !state.excludedIds.has(ticketId)
        : state.selectedTicketIds.has(ticketId);
      
      if (!isCurrentlySelected && !checkSelectionLimit(state.selectedCount + 1)) {
        return;
      }
      
      dispatch({ type: 'TOGGLE_TICKET', ticketId });
    }, [state, checkSelectionLimit]),
    
    isTicketSelected: useCallback((ticketId: string): boolean => {
      if (state.isSelectingAll) {
        return !state.excludedIds.has(ticketId);
      }
      return state.selectedTicketIds.has(ticketId);
    }, [state.isSelectingAll, state.excludedIds, state.selectedTicketIds]),
    
    getSelectedTickets: useCallback((): string[] => {
      if (state.isSelectingAll) {
        // In "select all" mode, we need to know all ticket IDs to exclude some
        // This should be provided by the parent component that knows all ticket IDs
        return [];
      }
      return Array.from(state.selectedTicketIds);
    }, [state.isSelectingAll, state.selectedTicketIds]),
    
    reset: useCallback(() => {
      dispatch({ type: 'RESET' });
    }, [])
  };

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+A / Cmd+A - Select All
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault();
        contextValue.selectAll();
      }
      
      // Escape - Clear selection
      if (event.key === 'Escape') {
        if (state.selectedCount > 0) {
          event.preventDefault();
          contextValue.selectNone();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [contextValue, state.selectedCount]);

  return (
    <BulkSelectionContext.Provider value={contextValue}>
      {children}
    </BulkSelectionContext.Provider>
  );
};

// Hook to use bulk selection
export const useBulkSelection = (): BulkSelectionContextValue => {
  const context = useContext(BulkSelectionContext);
  if (!context) {
    throw new Error('useBulkSelection must be used within a BulkSelectionProvider');
  }
  return context;
};

// Hook to update total count (for pagination)
export const useBulkSelectionTotalCount = (totalCount: number) => {
  const context = useContext(BulkSelectionContext);
  if (!context) {
    throw new Error('useBulkSelectionTotalCount must be used within a BulkSelectionProvider');
  }

  useEffect(() => {
    if (totalCount !== context.selectionState.totalCount) {
      // Update total count in reducer
      // This is a bit of a hack since we don't expose dispatch directly
      // In a real implementation, you might want to expose this through the context
    }
  }, [totalCount, context]);
};